import {
  ApiResponse,
  CalendarEvent,
  CreateMeetingData,
  Meeting,
  MeetingListParams,
  MeetingListResponse,
  UpdateMeetingData,
} from '../types';
import { apiService } from './api';

class MeetingService {
  private getEndpoint(projectId?: string): string {
    return projectId ? `/projects/${projectId}/meetings` : '/meetings';
  }

  /**
   * Create a new meeting
   */
  async createMeeting(data: CreateMeetingData, projectId?: string): Promise<ApiResponse<Meeting>> {
    try {
      return await apiService.post<Meeting>(this.getEndpoint(projectId), data);
    } catch (error: any) {
      console.error('Meeting Creation Error:', {
        endpoint: this.getEndpoint(projectId),
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
      });

      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create meeting',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Get meetings (global or project-specific)
   */
  async getMeetings(
    projectId?: string,
    params?: MeetingListParams
  ): Promise<ApiResponse<MeetingListResponse>> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.visibility) queryParams.append('visibility', params.visibility);
      if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
      if (params?.tags && params.tags.length > 0) {
        params.tags.forEach(tag => queryParams.append('tags', tag));
      }
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params?.view) queryParams.append('view', params.view);
      if (params?.calendarView) queryParams.append('calendarView', params.calendarView);

      const url = `${this.getEndpoint(projectId)}${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

      return await apiService.get<MeetingListResponse>(url);
    } catch (error: any) {
      console.error('Meeting API Error:', {
        endpoint: this.getEndpoint(projectId),
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
      });

      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch meetings',
      };
    }
  }

  /**
   * Get calendar events (global or project-specific)
   */
  async getCalendarEvents(
    projectId?: string,
    params: {
      dateFrom?: string;
      dateTo?: string;
      calendarView?: 'day' | 'week' | 'month';
    } = {}
  ): Promise<ApiResponse<CalendarEvent[]>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('view', 'calendar');

      if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params.calendarView) queryParams.append('calendarView', params.calendarView);

      const url = `${this.getEndpoint(projectId)}?${queryParams.toString()}`;

      return await apiService.get<CalendarEvent[]>(url);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch calendar events',
      };
    }
  }

  /**
   * Get a single meeting
   */
  async getMeeting(
    projectId: string | undefined,
    meetingId: string
  ): Promise<ApiResponse<Meeting>> {
    try {
      return await apiService.get<Meeting>(`${this.getEndpoint(projectId)}/${meetingId}`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch meeting',
      };
    }
  }

  /**
   * Update a meeting
   */
  async updateMeeting(
    projectId: string | undefined,
    meetingId: string,
    data: UpdateMeetingData,
    editType: 'this_only' | 'this_and_future' | 'all_series' = 'this_only'
  ): Promise<ApiResponse<Meeting>> {
    try {
      const updateData = { ...data, editType };
      return await apiService.put<Meeting>(
        `${this.getEndpoint(projectId)}/${meetingId}`,
        updateData
      );
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update meeting',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Cancel a meeting
   */
  async cancelMeeting(
    projectId: string | undefined,
    meetingId: string,
    reason?: string,
    editType: 'this_only' | 'this_and_future' | 'all_series' = 'this_only'
  ): Promise<ApiResponse<void>> {
    try {
      return await apiService.post<void>(`${this.getEndpoint(projectId)}/${meetingId}/cancel`, {
        reason,
        editType,
      });
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to cancel meeting',
      };
    }
  }

  /**
   * Delete a meeting
   */
  async deleteMeeting(
    projectId: string | undefined,
    meetingId: string,
    editType: 'this_only' | 'this_and_future' | 'all_series' = 'this_only'
  ): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>(
        `${this.getEndpoint(projectId)}/${meetingId}?editType=${editType}`
      );
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete meeting',
      };
    }
  }

  /**
   * Download meeting ICS file
   */
  async downloadICS(projectId: string | undefined, meetingId: string): Promise<void> {
    try {
      // Create download link directly
      const link = document.createElement('a');
      const token = localStorage.getItem('auth_token');
      const url = `${apiService.get.prototype.constructor.name === 'Function' ? process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000' : ''}${this.getEndpoint(projectId)}/${meetingId}/ics`;

      link.href = url;
      link.download = `meeting-${meetingId}.ics`;
      if (token) {
        // For authenticated requests, we need to handle this differently
        fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
          .then(response => response.blob())
          .then(blob => {
            const objectUrl = window.URL.createObjectURL(blob);
            link.href = objectUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
          })
          .catch(error => {
            console.error('Failed to download ICS file:', error);
            throw error;
          });
      } else {
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to download ICS file:', error);
      throw error;
    }
  }

  /**
   * Add attachment to meeting
   */
  async addAttachment(
    projectId: string,
    meetingId: string,
    attachmentData: {
      fileName: string;
      originalName: string;
      fileUrl: string;
      fileType: string;
      fileSize: number;
      s3Key?: string;
    }
  ): Promise<ApiResponse<Meeting>> {
    try {
      return await apiService.post<Meeting>(
        `${this.getEndpoint(projectId)}/${meetingId}/attachments`,
        attachmentData
      );
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add attachment',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Upload file for meeting attachment
   */
  async uploadFile(
    file: File,
    projectId: string,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);

      const config: any = {};
      if (onProgress) {
        config.onUploadProgress = (progressEvent: any) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        };
      }

      const response = await apiService.upload('/files/upload', formData, config);
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload file',
      };
    }
  }

  /**
   * Respond to meeting invitation (for external users)
   */
  async respondToInvitation(
    meetingId: string,
    token: string,
    response: 'accepted' | 'declined' | 'maybe'
  ): Promise<ApiResponse<void>> {
    try {
      return await apiService.post<void>(`/meetings/external/${meetingId}/respond/${token}`, {
        response,
      });
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to respond to invitation',
      };
    }
  }

  /**
   * Get meeting categories from configuration
   */
  async getMeetingCategories(): Promise<ApiResponse<any[]>> {
    try {
      return await apiService.post<any[]>('/configurations/bulk', {
        keys: ['meeting-types'],
      });
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch meeting categories',
      };
    }
  }

  /**
   * Get meeting tags (could be from configuration or project-specific)
   */
  async getMeetingTags(_projectId?: string): Promise<ApiResponse<string[]>> {
    try {
      // This could be enhanced to fetch project-specific tags
      // For now, return common meeting tags
      const commonTags = [
        'planning',
        'standup',
        'review',
        'client',
        'team',
        'progress',
        'milestone',
        'kickoff',
        'retrospective',
        'design',
        'technical',
        'budget',
        'safety',
        'inspection',
      ];

      return {
        success: true,
        data: commonTags,
        message: 'Meeting tags retrieved successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch meeting tags',
      };
    }
  }

  /**
   * Validate meeting data before submission
   */
  validateMeetingData(data: CreateMeetingData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required fields
    if (!data.title?.trim()) {
      errors.push('Meeting title is required');
    }

    if (!data.startDateTime) {
      errors.push('Start date and time is required');
    }

    if (!data.endDateTime) {
      errors.push('End date and time is required');
    }

    if (!data.visibility) {
      errors.push('Meeting visibility is required');
    }

    if (!data.scheduleType) {
      errors.push('Schedule type is required');
    }

    // Date validation
    if (data.startDateTime && data.endDateTime) {
      const startDate = new Date(data.startDateTime);
      const endDate = new Date(data.endDateTime);

      if (startDate >= endDate) {
        errors.push('End date must be after start date');
      }

      if (startDate < new Date()) {
        errors.push('Meeting cannot be scheduled in the past');
      }
    }

    // Email validation for external users
    if (data.externalUsers && data.externalUsers.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      data.externalUsers.forEach((user, index) => {
        if (!user.email || !emailRegex.test(user.email)) {
          errors.push(`Invalid email address for external user ${index + 1}`);
        }
      });
    }

    // Recurrence validation
    if (data.scheduleType !== 'one_time' && !data.recurrence) {
      errors.push('Recurrence settings are required for recurring meetings');
    }

    if (data.recurrence && data.scheduleType !== 'one_time') {
      if (
        data.recurrence.endType === 'after' &&
        (!data.recurrence.endAfterOccurrences || data.recurrence.endAfterOccurrences < 1)
      ) {
        errors.push('Number of occurrences must be at least 1');
      }

      if (
        data.recurrence.endType === 'until' &&
        (!data.recurrence.endDate ||
          new Date(data.recurrence.endDate) <= new Date(data.startDateTime))
      ) {
        errors.push('End date must be after start date');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format meeting for display
   */
  formatMeetingForDisplay(meeting: Meeting): Meeting {
    return {
      ...meeting,
      startDateTime: new Date(meeting.startDateTime),
      endDateTime: new Date(meeting.endDateTime),
      createdAt: new Date(meeting.createdAt),
      updatedAt: new Date(meeting.updatedAt),
      cancelledAt: meeting.cancelledAt ? new Date(meeting.cancelledAt) : undefined,
      recurringInstanceDate: meeting.recurringInstanceDate
        ? new Date(meeting.recurringInstanceDate)
        : undefined,
    };
  }

  /**
   * Convert meeting to calendar event
   */
  meetingToCalendarEvent(meeting: Meeting): CalendarEvent {
    return {
      id: meeting.id || meeting._id!,
      title: meeting.title,
      start: new Date(meeting.startDateTime),
      end: new Date(meeting.endDateTime),
      allDay: false,
      resource: {
        id: meeting.id || meeting._id!,
        status: meeting.status,
        visibility: meeting.visibility,
        location: meeting.location,
        category: meeting.category,
        tags: meeting.tags,
        createdBy: meeting.createdById,
      },
    };
  }
}

export const meetingService = new MeetingService();
export default meetingService;
